import React, {useEffect, useReducer, useState} from 'react'
import SelectUser, {OTHER, SELF} from './SelectUser/SelectUser'
import VisitorsList from '../VisitorsList/VisitorsList'
import Confirmation from '../../../Confirmation/Confirmation'
import Summary from './Summary/Summary'
import {fetchEmployee} from '../../../../utils/employees'
import {createWorkspaceBooking} from '../../../../api/workspace'
import styled from 'styled-components'
import Clock from '../../../Clock/Clock'
import HeaderTitle from '../../../Header/HeaderTitle'
import Header from '../../../Header/Header'
import HeaderButton from '../../../Header/HeaderButton'
import {useWorkspaceState} from '../../../../context/WorkspaceContext'
import {generateRecurrenceXml} from '../../../../utils/recurrence'
import {_L, useLocaleState} from '../../../../context/LocaleContext'
import {
  setDrawingHighlights,
  useDrawingDispatch
} from '../../../../context/DrawingContext'
import {useKioskTimer} from '../../../../hooks/kiosk-timer'
import {useUIDispatch, useUIState} from '../../../../context/UIContext'
import {KIOSK_CONTAINER} from '../../../../constants/const'

const SELECT_USER = 'SELECT_USER'
const VISITORS_LIST = 'VISITORS_LIST'
const CONFIRMATION = 'CONFIRMATION'
const SUMMARY = 'SUMMARY'

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  background-color: #eff0f3;
  box-sizing: border-box;
`

const StyledContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: calc(100vh - 80px);
  background-color: #eff0f3;
`

const HeaderButtons = styled.div`
  box-sizing: border-box;
`

const initialState = {
  booking: {
    visitorId: '',
    visitorName: '',
    emId: '',
    dvId: '',
    dpId: '',
    isVisitor: false
  },
  employee: {},
  loading: false,
  error: null
}

const reducer = (state, action) => {
  switch (action.type) {
    case 'setBooking':
      return {
        ...state,
        booking: {
          ...state.booking,
          ...action.payload
        }
      }
    case 'setEmployee':
      return {
        ...state,
        employee: action.payload
      }
    case 'setLoading':
      return {
        ...state,
        loading: action.payload,
        error: null
      }
    case 'setError':
      return {
        ...state,
        error: action.payload,
        loading: false
      }
    default:
      throw new Error()
  }
}

const viewStates = {
  SELECT_USER: {
    prev: () => null,
    next: ({props}) => {
      const {type} = props
      if (type === OTHER) {
        return VISITORS_LIST
      } else {
        return SUMMARY
      }
    }
  },
  VISITORS_LIST: {
    prev: () => SELECT_USER,
    next: () => SUMMARY
  },
  SUMMARY: {
    prev: () => VISITORS_LIST,
    next: () => CONFIRMATION
  },
  CONFIRMATION: {
    prev: () => SUMMARY,
    next: () => null
  }
}

const useViewState = ({viewStates, initialView}) => {
  const [viewState, setViewState] = useState(initialView)

  const dispatchViewState = ({action, props}) => {
    let nextAction = viewStates[viewState][action].apply(null, [{props}])
    setViewState(nextAction)
  }

  return {
    viewState,
    dispatchViewState
  }
}

const WorkspacesSequence = ({user, roomCode, goToMain, isQrView, onCancel}) => {

  const [state, dispatch] = useReducer(reducer, initialState)
  const localeState = useLocaleState()
  const drawingDispatch = useDrawingDispatch()

  const initialView = user.guest ? VISITORS_LIST : SELECT_USER

  const {viewState, dispatchViewState} = useViewState({
    viewStates,
    initialView
  })

  const {search} = useWorkspaceState()
  const {homeFloor, isKiosk} = useUIState()
  const uiDispatch = useUIDispatch()

  useEffect(
    () => {
      setDrawingHighlights({
        dispatch: drawingDispatch,
        assetType: 'rm',
        reset: true
      })
      if (user.guest) {
        // Render Visitor List if user is a guest
        // dispatchViewState({action: 'next'})
      } else {
        let emId = user.employee.id
        if (emId) {
          fetchEmployee({emId}).then(employee => {
            dispatch({type: 'setEmployee', payload: employee})
          })
        }
      }
    },
    [user, drawingDispatch]
  )

  const timeoutExpired = () => {
    uiDispatch({type: 'setContent', payload: {content: KIOSK_CONTAINER}})
    uiDispatch({type: 'setActiveFloor', payload: homeFloor})
  }

  const {message, onClickHandler} = useKioskTimer({
    enable: isKiosk,
    inUseDelay: 60000,
    timeoutDelay: 10000,
    timeoutExpirationHandler: timeoutExpired
  })

  const onBook = () => {
    const {visitorId, visitorName, emId, dvId, dpId} = booking
    const {blId, flId, rmId} = roomCode
    const {date_start, date_end, dayPart, dv_id, dp_id, recurrenceType} = search

    let bookingEmployee = emId || user.employee.id

    let bookings = [
      {
        values: {
          'rmpct.bl_id': blId,
          'rmpct.fl_id': flId,
          'rmpct.rm_id': rmId,
          'rmpct.em_id': bookingEmployee,
          'rmpct.dv_id': dvId,
          'rmpct.dp_id': dpId,
          'rmpct.confirmed': 1,
          'rmpct.visitor_id': visitorId,
          'rmpct.visitor_name': visitorName
        }
      }
    ]

    let recurrence = ''

    if (recurrenceType) {
      recurrence =
        recurrenceType === 'none' ? '' : generateRecurrenceXml(search)
    }

    // Construct parameter to pass to WFR
    const params = {
      dv_id,
      dp_id,
      dayPart,
      date_start: date_start,
      date_end: date_end,
      recurrence
    }

    dispatch({type: 'setLoading', payload: true})
    createWorkspaceBooking({params, bookings})
      .then(() => {
        dispatch({type: 'setLoading', payload: false})
        dispatchViewState({action: 'next'})
      })
      .catch(error => {
        dispatch({type: 'setError', payload: error})
      })
  }

  const onGoToMain = () => {
    goToMain({roomCode})
  }

  const onSelectVisitor = visitor => {
    const {
      emId,
      dvId,
      dpId,
      visitorId,
      name: visitorName,
      isVisitor,
      emTitle,
      phone,
      email
    } = visitor

    if (isVisitor === '0') {
      // Employee
      dispatch({
        type: 'setBooking',
        payload: {
          emId,
          dvId,
          dpId,
          isVisitor: false
        }
      })
      // TODO: Combine employee with booking object
      dispatch({
        type: 'setEmployee',
        payload: {
          emId,
          emTitle,
          phone,
          email
        }
      })
    } else {
      // Visitor
      dispatch({
        type: 'setBooking',
        payload: {
          visitorId,
          visitorName,
          dvId,
          dpId,
          isVisitor: true
        }
      })
    }

    dispatchViewState({action: 'next'})
  }

  const newVisitorAdded = ({
    visitorName,
    visitorId,
    dvId,
    dpId,
    email,
    company,
    visitorPhone,
    isVisitor
  }) => {
    dispatch({
      type: 'setBooking',
      payload: {
        visitorId,
        visitorName,
        dvId,
        dpId,
        isVisitor,
        visitorEmail: email,
        company,
        visitorPhone
      }
    })

    dispatchViewState({action: 'next'})
  }

  const onSelectUserType = type => {
    if (type === SELF) {
      const {emId, dvId, dpId} = employee
      dispatch({type: 'setBooking', payload: {emId, dvId, dpId}})
      dispatch({type: 'setEmployee', payload: employee})
    }

    dispatchViewState({action: 'next', props: {type}})
  }

  const renderContent = () => {
    const views = {
      SELECT_USER: (
        <SelectUser employee={employee} onSelectUser={onSelectUserType} />
      ),
      VISITORS_LIST: (
        <VisitorsList
          onSelectVisitor={onSelectVisitor}
          newVisitorCreated={newVisitorAdded}
          label={_L({key: 'Booking space for:', localeState})}
        />
      ),
      SUMMARY: (
        <Summary
          booking={booking}
          employee={employee}
          workspaceSearch={search}
          onBook={onBook}
          loading={loading}
          error={error}
        />
      ),
      CONFIRMATION: (
        <Confirmation
          title={_L({key: 'Workspace is successfully booked!', localeState})}
          subTitle={_L({
            key: 'Confirmation was sent to your email',
            localeState
          })}
          goToMain={onGoToMain}
        />
      )
    }

    return views[viewState]
  }

  const onHeaderSignOn = () => {}
  const onHeaderCancel = () => onCancel({roomCode})

  const isGuest = user.guest
  const signOnLabel = isGuest
    ? _L({key: 'Sign On', localeState})
    : _L({key: 'Sign Off', localeState})

  const renderHeaderButton = () => (
    <HeaderButtons>
      {isQrView && (
        <HeaderButton onClick={onHeaderSignOn} label={signOnLabel} />
      )}
      <HeaderButton
        onClick={onHeaderCancel}
        label={_L({key: 'Cancel', localeState})}
      />
    </HeaderButtons>
  )

  const {employee, booking, loading, error} = state

  return (
    <Container onClick={onClickHandler}>
      <Header
        leftContent={<Clock />}
        centerContent={
          <HeaderTitle title={_L({key: 'Workspace Booking', localeState})} />
        }
        rightContent={renderHeaderButton()}
      />
      <StyledContent>{renderContent()}</StyledContent>
      {message}
    </Container>
  )
}

export default WorkspacesSequence
